import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	output,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { Currency, currencyLucideIcon } from '@marmon/shared/types/common';
import { TuiHint, TuiIcon } from '@taiga-ui/core';
import { TuiSegmented } from '@taiga-ui/kit';
import { IconComponent } from '../../icon/icon.component';
import { MarmonIconType } from '../../icon/icons.const';

export type AnalysisCardType = 'percent' | 'dollar' | 'sum';
type AnalysisCardOption = {
	icon: string;
	type: AnalysisCardType;
};

const AnalysisCardTypeOrder: AnalysisCardType[] = ['percent', 'dollar', 'sum'];

const AnalysisCardTypeIcons: Record<
	Exclude<AnalysisCardType, 'dollar'>,
	string
> = {
	percent: '@tui.percent',
	sum: '@tui.hash',
};

@Component({
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [IconComponent, TranslocoPipe, TuiSegmented, TuiIcon, TuiHint],
	selector: 'mar-analysis-card',
	templateUrl: 'analysis-card.component.html',
	styleUrl: 'analysis-card.component.scss',
})
export class AnalysisCardComponent {
	readonly icon = input.required<MarmonIconType>();
	readonly label = input.required<string>();
	readonly tooltip = input<string | null>(null);

	/**
	 * Analysis currency used to pick the Lucide icon on the currency toggle.
	 * Omitted currencies use the dollar sign.
	 */
	readonly currency = input<Currency | null | undefined>(null);

	/**
	 * Requires at least 2 options
	 */
	readonly types = input<AnalysisCardType[] | undefined>();

	readonly typeChange = output<AnalysisCardType | undefined>();

	readonly cardTypes = computed<AnalysisCardOption[]>(() => {
		const types = this.types();
		if (!types || types.length <= 1) return [];

		const icons: Record<AnalysisCardType, string> = {
			...AnalysisCardTypeIcons,
			dollar: `@tui.${currencyLucideIcon(this.currency())}`,
		};

		return AnalysisCardTypeOrder.filter((type) => types.includes(type)).map(
			(type) => ({ type, icon: icons[type] }),
		);
	});

	activeTypeIndex = 0;

	get activeType() {
		const type = this.cardTypes()[this.activeTypeIndex];
		if (!type) throw Error('Index of card types is out of range');
		return type;
	}

	onTypeChange() {
		this.typeChange.emit(this.activeType.type);
	}
}
